import React, { useCallback, useEffect, useRef, useState } from "react";
import { GlobalButton } from "./GlobalFormFields";
import { GlobalModal } from "../GlobalModal";
import SafeMediaImage from "../SafeMediaImage";
import { useEyeBlinkLiveness } from "../hooks/useEyeBlinkLiveness";
import { compressUserPhoto } from "../../utils/compressImage";
import { resolveMediaUrl } from "../../utils/resolveMediaUrl";
import "./GlobalPhotoPicker.css";

export const PHOTO_ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

function IconUser(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden {...props}>
      <circle cx="12" cy="8" r="4" />
      <path d="M5 20c0-3.3 3.1-6 7-6s7 2.7 7 6" />
    </svg>
  );
}

function IconUpload(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden {...props}>
      <path d="M12 16V4m0 0 7 7m-7-7L5 11" />
      <path d="M4 20h16" />
    </svg>
  );
}

function IconCamera(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden {...props}>
      <path d="M4 8h3l2-3h6l2 3h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2z" />
      <circle cx="12" cy="14" r="3.5" />
    </svg>
  );
}

function IconCameraSmall(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden {...props}>
      <path d="M4 8h3l2-3h6l2 3h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2z" />
      <circle cx="12" cy="14" r="2.5" />
    </svg>
  );
}

function validatePhotoFile(file) {
  if (!file) return "";
  if (!PHOTO_ALLOWED_TYPES.includes(file.type)) {
    return "Upload a JPG or PNG image";
  }
  return "";
}

export function GlobalPhotoPicker({
  id = "user-photo",
  label = "Profile photo",
  file,
  onChange,
  existingUrl = "",
  error = "",
  baseAlertId,
  optionalHint = "JPG or PNG. Final size is enforced by admin Theme settings on the server.",
  layout = "card",
}) {
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [previewUrl, setPreviewUrl] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [capturedDataUrl, setCapturedDataUrl] = useState("");
  const [blinkSession, setBlinkSession] = useState(0);

  const blinkLiveness = useEyeBlinkLiveness({
    active: cameraOpen && !capturedDataUrl && !cameraError,
    videoRef,
    resetKey: blinkSession,
  });

  const revokePreview = useCallback((url) => {
    if (url && url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  }, []);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl((prev) => {
        revokePreview(prev);
        return url;
      });
      return () => revokePreview(url);
    }
    setPreviewUrl((prev) => {
      revokePreview(prev);
      return resolveMediaUrl(existingUrl) || "";
    });
    return undefined;
  }, [file, existingUrl, revokePreview]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const closeCamera = useCallback(() => {
    stopCamera();
    setCameraOpen(false);
    setCapturedDataUrl("");
    setCameraError("");
  }, [stopCamera]);

  useEffect(() => {
    if (!cameraOpen || capturedDataUrl) return undefined;

    let cancelled = false;
    setCameraError("");

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => null);
        }
      } catch {
        setCameraError(
          "Could not access the camera. Check permissions or upload from your device.",
        );
      }
    })();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [cameraOpen, capturedDataUrl, stopCamera]);

  const applyFile = async (nextFile) => {
    try {
      const compressed = await compressUserPhoto(nextFile);
      const validationError = validatePhotoFile(compressed);
      if (validationError) {
        onChange?.(null, validationError);
        return;
      }
      onChange?.(compressed, "");
    } catch {
      onChange?.(null, "Could not compress photo");
    }
  };

  const handleDevicePick = async (e) => {
    const picked = e.target.files?.[0] || null;
    e.target.value = "";
    if (!picked) return;
    await applyFile(picked);
  };

  const openCamera = () => {
    setCapturedDataUrl("");
    setCameraError("");
    setBlinkSession((n) => n + 1);
    setCameraOpen(true);
  };

  const handleCaptureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    setCapturedDataUrl(canvas.toDataURL("image/jpeg", 0.92));
    stopCamera();
  };

  const handleUseCapturedPhoto = async () => {
    if (!capturedDataUrl) return;
    try {
      const res = await fetch(capturedDataUrl);
      const blob = await res.blob();
      const nextFile = new File([blob], `capture-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });
      await applyFile(nextFile);
      closeCamera();
    } catch {
      onChange?.(null, "Could not process captured photo");
    }
  };

  const handleClear = () => {
    onChange?.(null, "");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const errorId = error ? `${id}-error` : undefined;
  const hasPhoto = Boolean(previewUrl);
  const fileName = file?.name;

  return (
    <div
      className={`gpp-field gff-field gpp-field--${layout}${error ? " gpp-field--error" : ""}`}
    >
      <div className="gpp-card">
        <div className="gpp-avatar-wrap">
          <button
            type="button"
            className="gpp-avatar-btn"
            onClick={() => fileInputRef.current?.click()}
            aria-label="Choose profile photo from device"
          >
            <div className={`gpp-avatar-ring${hasPhoto ? " gpp-avatar-ring--filled" : ""}`}>
              {hasPhoto ? (
                <SafeMediaImage
                  src={previewUrl}
                  alt=""
                  className="gpp-avatar-img"
                  fallbackClassName="gpp-avatar-img safe-media-fallback--avatar"
                  iconSize={22}
                />
              ) : (
                <span className="gpp-avatar-empty">
                  <IconUser className="gpp-avatar-empty__icon" />
                  <span className="gpp-avatar-empty__text">Add photo</span>
                </span>
              )}
            </div>
            <span className="gpp-avatar-badge" aria-hidden>
              <IconCameraSmall />
            </span>
          </button>
        </div>

        <div className="gpp-body">
          <div className="gpp-head">
            <p className="gpp-label" id={`${id}-label`}>
              {label}
            </p>
          </div>
          <p className="gpp-hint">{optionalHint}</p>

          <div className="gpp-actions">
            <input
              ref={fileInputRef}
              id={id}
              type="file"
              accept="image/jpeg,image/png"
              className="gpp-file-input"
              onChange={handleDevicePick}
              aria-describedby={
                [errorId, baseAlertId, `${id}-label`].filter(Boolean).join(" ") || undefined
              }
            />
            <button
              type="button"
              className="gpp-action-btn gpp-action-btn--primary"
              onClick={() => fileInputRef.current?.click()}
            >
              <IconUpload />
              Choose from device
            </button>
            <button type="button" className="gpp-action-btn" onClick={openCamera}>
              <IconCamera />
              Capture photo
            </button>
            {hasPhoto ? (
              <button
                type="button"
                className="gpp-action-btn gpp-action-btn--ghost"
                onClick={handleClear}
              >
                Remove
              </button>
            ) : null}
          </div>

          {fileName ? (
            <p className="gpp-meta">Selected: {fileName}</p>
          ) : null}
        </div>
      </div>

      {error ? (
        <span id={errorId} className="gff-error" role="alert" style={{ marginTop: "0.5rem" }}>
          {error}
        </span>
      ) : null}

      <GlobalModal
        title="Capture photo"
        description="Center your face in the frame, blink once, then tap Capture."
        open={cameraOpen}
        onClose={closeCamera}
        size="md"
        footer={
          <div className="gpp-camera-footer">
            {capturedDataUrl ? (
              <>
                <GlobalButton
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setCapturedDataUrl("");
                    setBlinkSession((n) => n + 1);
                  }}
                >
                  Retake
                </GlobalButton>
                <GlobalButton type="button" variant="primary" onClick={handleUseCapturedPhoto}>
                  Use photo
                </GlobalButton>
              </>
            ) : (
              <>
                <GlobalButton type="button" variant="ghost" onClick={closeCamera}>
                  Cancel
                </GlobalButton>
                <GlobalButton
                  type="button"
                  variant="primary"
                  onClick={handleCaptureFrame}
                  disabled={!!cameraError || !blinkLiveness.canCapture}
                >
                  Capture
                </GlobalButton>
              </>
            )}
          </div>
        }
      >
        <div className="gpp-camera-body">
          {cameraError ? (
            <p className="gff-error" role="alert">
              {cameraError}
            </p>
          ) : null}
          {capturedDataUrl ? (
            <img src={capturedDataUrl} alt="Captured preview" className="gpp-camera-preview" />
          ) : (
            <>
              <video ref={videoRef} className="gpp-camera-video" playsInline muted />
              {!cameraError ? (
                <p
                  className={`gpp-camera-blink-status${
                    blinkLiveness.blinkVerified ? " gpp-camera-blink-status--ok" : ""
                  }`}
                  aria-live="polite"
                >
                  {blinkLiveness.statusText}
                </p>
              ) : null}
            </>
          )}
          <canvas ref={canvasRef} className="gpp-camera-canvas" aria-hidden />
        </div>
      </GlobalModal>
    </div>
  );
}
